import { StandardEvents } from '@shopify/events';

const GALLERY_TRANSITION = 420;
const SWIPE_THRESHOLD = 42;

class SkCustomProduct extends HTMLElement {
  #slides = [];
  #variants = [];
  #activeMediaId = '';
  #timer = 0;
  #observer;
  #stickyObserver;
  #scrollContainer;
  #isVisible = false;
  #userPaused = false;
  #isAnimating = false;
  #abortController = new AbortController();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.#activeMediaId = this.#slides.find((slide) => slide.classList.contains('is-active'))?.dataset.mediaId || '';
    this.#variants = this.#readVariants();

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    this.#scrollContainer = document.querySelector('.page-wrapper') || window;
    this.#scrollContainer.addEventListener('scroll', this.#updateStickyVisibility, { signal, passive: true });
    window.addEventListener('scroll', this.#updateStickyVisibility, { signal, passive: true });
    window.addEventListener('resize', this.#updateStickyVisibility, { signal, passive: true });

    this.#bindSwipe(this.querySelector('[data-main-media]'));
    this.#bindSwipe(this.querySelector('[data-thumb-viewport]'));
    this.#updateOptionAvailability();

    this.#observer = new IntersectionObserver(this.#handleIntersection, { threshold: [0, 0.15] });
    this.#observer.observe(this);

    this.#setupStickyAddToCart();
  }

  disconnectedCallback() {
    this.#clearTimer();
    this.#observer?.disconnect();
    this.#stickyObserver?.disconnect();
    this.#abortController.abort();
  }

  #readVariants() {
    const node = this.querySelector('[data-variants-json]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent || '[]');
    } catch (error) {
      console.warn('[SK custom product] Variant data could not be read.', error);
      return [];
    }
  }

  #handleIntersection = ([entry]) => {
    this.#isVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);
    if (this.#isVisible) this.#scheduleAutoplay();
    else this.#clearTimer();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#clearTimer();
    else this.#scheduleAutoplay();
  };

  #scheduleAutoplay() {
    this.#clearTimer();
    if (
      !this.#isVisible ||
      this.#userPaused ||
      document.hidden ||
      this.dataset.autoplay !== 'true' ||
      this.#reducedMotion.matches ||
      this.#slides.length < 2
    ) return;

    const interval = Math.max(3000, Number(this.dataset.interval) || 5000);
    this.#timer = window.setTimeout(() => {
      this.#moveForward(false);
      this.#scheduleAutoplay();
    }, interval);
  }

  #clearTimer() {
    if (!this.#timer) return;
    window.clearTimeout(this.#timer);
    this.#timer = 0;
  }

  #pauseForUser() {
    this.#userPaused = true;
    this.#clearTimer();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('[data-option]');
    if (option && this.contains(option)) {
      this.#pauseForUser();
      this.#selectOption(option);
      return;
    }

    const thumb = event.target.closest('[data-thumb]');
    if (thumb && this.contains(thumb)) {
      this.#pauseForUser();
      this.#moveThumbToFront(thumb);
      return;
    }

    if (event.target.closest('[data-gallery-prev]')) {
      this.#pauseForUser();
      this.#moveBackward();
      return;
    }

    if (event.target.closest('[data-gallery-next]')) {
      this.#pauseForUser();
      this.#moveForward(true);
      return;
    }

    const buyNow = event.target.closest('[data-buy-now]');
    if (buyNow instanceof HTMLButtonElement) {
      this.#buyNow(buyNow);
      return;
    }

    const stickyVariantToggle = event.target.closest('[data-sticky-variant-toggle]');
    if (stickyVariantToggle instanceof HTMLButtonElement && !stickyVariantToggle.disabled) {
      this.#toggleStickyVariantMenu(stickyVariantToggle);
      return;
    }

    const stickyVariantOption = event.target.closest('[data-sticky-variant-option]');
    if (stickyVariantOption instanceof HTMLButtonElement && !stickyVariantOption.disabled) {
      this.#selectStickyVariant(stickyVariantOption);
      return;
    }

    const stickyAdd = event.target.closest('[data-sticky-add]');
    if (stickyAdd instanceof HTMLButtonElement) {
      this.#submitStickyAdd(stickyAdd);
      return;
    }

    if (event.target.closest('[data-main-media]')) this.#pauseForUser();
  };

  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    this.#closeStickyVariantMenu();
  };

  #setupStickyAddToCart() {
    const sticky = this.querySelector('[data-sticky-atc]');
    const mainAdd = this.querySelector('[data-add-button]');
    if (!(sticky instanceof HTMLElement) || !(mainAdd instanceof HTMLButtonElement)) return;

    const observerRoot = this.#scrollContainer instanceof Element ? this.#scrollContainer : null;
    this.#stickyObserver = new IntersectionObserver(this.#updateStickyVisibility, {
      root: observerRoot,
      threshold: [0, 0.01, 1],
    });
    this.#stickyObserver.observe(mainAdd);
    this.#syncStickyVariant(this.#currentVariant());
    window.requestAnimationFrame(this.#updateStickyVisibility);
  }

  #updateStickyVisibility = () => {
    const sticky = this.querySelector('[data-sticky-atc]');
    const mainAdd = this.querySelector('[data-add-button]');
    if (!(sticky instanceof HTMLElement) || !(mainAdd instanceof HTMLButtonElement)) return;

    const rect = mainAdd.getBoundingClientRect();
    const scrollBounds =
      this.#scrollContainer instanceof Element
        ? this.#scrollContainer.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };
    const mainButtonVisible = rect.bottom > scrollBounds.top && rect.top < scrollBounds.bottom;
    const forceVisible = this.dataset.stickyAlwaysVisible === 'true';
    const shouldShow = forceVisible || !mainButtonVisible;

    sticky.classList.toggle('is-visible', shouldShow);
    sticky.setAttribute('aria-hidden', String(!shouldShow));
    if (!shouldShow) this.#closeStickyVariantMenu();
  };

  #toggleStickyVariantMenu(toggle) {
    const menu = this.querySelector('[data-sticky-variant-menu]');
    if (!(menu instanceof HTMLElement)) return;
    const willOpen = menu.hidden;
    menu.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', String(willOpen));
  }

  #closeStickyVariantMenu() {
    const toggle = this.querySelector('[data-sticky-variant-toggle]');
    const menu = this.querySelector('[data-sticky-variant-menu]');
    if (menu instanceof HTMLElement) menu.hidden = true;
    if (toggle instanceof HTMLButtonElement) toggle.setAttribute('aria-expanded', 'false');
  }

  #selectStickyVariant(option) {
    const variant = this.#variants.find((candidate) => String(candidate.id) === option.dataset.variantId);
    if (!variant) return;

    this.querySelectorAll('[data-option-group]').forEach((group, index) => {
      group.querySelectorAll('[data-option]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.optionValue === variant.options[index]));
      });
    });

    this.#applyVariant(variant);
    this.#updateOptionAvailability();
    this.#closeStickyVariantMenu();
  }

  #submitStickyAdd(stickyButton) {
    if (stickyButton.disabled || stickyButton.getAttribute('aria-busy') === 'true') return;
    const mainAdd = this.querySelector('[data-add-button]');
    if (!(mainAdd instanceof HTMLButtonElement) || mainAdd.disabled) return;

    stickyButton.setAttribute('aria-busy', 'true');
    stickyButton.disabled = true;
    mainAdd.click();
  }

  #currentVariant() {
    const variantInput = this.querySelector('[data-variant-input]');
    return this.#variants.find((candidate) => String(candidate.id) === variantInput?.value);
  }

  #syncStickyVariant(variant) {
    const label = this.querySelector('[data-sticky-variant-label]');
    const addButton = this.querySelector('[data-sticky-add]');
    const addLabel = this.querySelector('[data-sticky-add-label]');
    const optionButtons = this.querySelectorAll('[data-sticky-variant-option]');

    if (variant && label && !(variant.options.length === 1 && variant.options[0] === 'Default Title')) {
      label.textContent = variant.options.join(' / ');
    }
    optionButtons.forEach((option) => {
      option.setAttribute('aria-checked', String(Boolean(variant) && option.dataset.variantId === String(variant.id)));
    });
    if (addButton instanceof HTMLButtonElement) addButton.disabled = !variant?.available;
    if (addLabel) addLabel.textContent = variant?.available ? (addLabel.dataset.label || this.dataset.addLabel || 'Add to cart') : (this.dataset.soldOutLabel || 'Sold out');
  }

  #bindSwipe(element) {
    if (!(element instanceof HTMLElement)) return;
    let startX = 0;
    let startY = 0;
    let pointerId = null;

    element.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button > 0) return;
      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
    }, { signal: this.#abortController.signal });

    element.addEventListener('pointerup', (event) => {
      if (pointerId !== event.pointerId) return;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      pointerId = null;

      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      event.preventDefault();
      this.#pauseForUser();
      if (deltaX < 0) this.#moveForward(true);
      else this.#moveBackward();
    }, { signal: this.#abortController.signal });

    element.addEventListener('pointercancel', () => { pointerId = null; }, { signal: this.#abortController.signal });
  }

  #selectMedia(mediaId) {
    if (!mediaId) return;
    const slide = this.#slides.find((candidate) => candidate.dataset.mediaId === String(mediaId));
    if (!slide) return;

    this.#slides.forEach((candidate) => {
      const active = candidate === slide;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-hidden', String(!active));
    });

    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      const active = thumb.dataset.mediaId === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
    });
    this.#activeMediaId = String(mediaId);
  }

  #thumbStep() {
    const track = this.querySelector('[data-thumb-track]');
    const first = track?.querySelector('[data-thumb]');
    if (!(track instanceof HTMLElement) || !(first instanceof HTMLElement)) return 0;
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  #setTrackTransform(value, animated) {
    const track = this.querySelector('[data-thumb-track]');
    if (!(track instanceof HTMLElement)) return;
    track.style.transition = animated && !this.#reducedMotion.matches ? `transform ${GALLERY_TRANSITION}ms cubic-bezier(.22,.75,.24,1)` : 'none';
    track.style.transform = `translate3d(${value}px,0,0)`;
  }

  #moveForward(userInitiated) {
    if (this.#isAnimating || this.#slides.length < 2) return;
    if (userInitiated) this.#pauseForUser();
    const track = this.querySelector('[data-thumb-track]');
    const first = track?.querySelector('[data-thumb]');
    const next = first?.nextElementSibling;

    if (!(track instanceof HTMLElement) || !(first instanceof HTMLElement) || !(next instanceof HTMLElement)) {
      const current = Math.max(0, this.#slides.findIndex((slide) => slide.dataset.mediaId === this.#activeMediaId));
      this.#selectMedia(this.#slides[(current + 1) % this.#slides.length]?.dataset.mediaId);
      return;
    }

    this.#isAnimating = true;
    this.#selectMedia(next.dataset.mediaId);
    this.#setTrackTransform(-this.#thumbStep(), true);
    window.setTimeout(() => {
      track.append(first);
      this.#setTrackTransform(0, false);
      this.#isAnimating = false;
    }, this.#reducedMotion.matches ? 0 : GALLERY_TRANSITION);
  }

  #moveBackward() {
    if (this.#isAnimating || this.#slides.length < 2) return;
    this.#pauseForUser();
    const track = this.querySelector('[data-thumb-track]');
    const last = track?.lastElementChild;
    if (!(track instanceof HTMLElement) || !(last instanceof HTMLElement)) return;

    this.#isAnimating = true;
    track.prepend(last);
    this.#setTrackTransform(-this.#thumbStep(), false);
    track.getBoundingClientRect();
    this.#selectMedia(last.dataset.mediaId);
    requestAnimationFrame(() => this.#setTrackTransform(0, true));
    window.setTimeout(() => { this.#isAnimating = false; }, this.#reducedMotion.matches ? 0 : GALLERY_TRANSITION);
  }

  #moveThumbToFront(thumb) {
    if (!(thumb instanceof HTMLElement)) return;
    if (this.#isAnimating) {
      const mediaId = thumb.dataset.mediaId;
      this.#selectMedia(mediaId);
      window.setTimeout(() => {
        const currentThumb = Array.from(this.querySelectorAll('[data-thumb]')).find((candidate) => candidate.dataset.mediaId === mediaId);
        if (currentThumb) this.#moveThumbToFront(currentThumb);
      }, this.#reducedMotion.matches ? 0 : GALLERY_TRANSITION + 20);
      return;
    }
    const track = this.querySelector('[data-thumb-track]');
    if (!(track instanceof HTMLElement)) {
      this.#selectMedia(thumb.dataset.mediaId);
      return;
    }

    const thumbs = Array.from(track.querySelectorAll('[data-thumb]'));
    const index = thumbs.indexOf(thumb);
    this.#selectMedia(thumb.dataset.mediaId);
    if (index <= 0) return;

    this.#isAnimating = true;
    this.#setTrackTransform(-this.#thumbStep() * index, true);
    window.setTimeout(() => {
      thumbs.slice(0, index).forEach((item) => track.append(item));
      this.#setTrackTransform(0, false);
      this.#isAnimating = false;
    }, this.#reducedMotion.matches ? 0 : GALLERY_TRANSITION);
  }

  #selectedOptions() {
    const groups = Array.from(this.querySelectorAll('[data-option-group]'));
    return groups.map((group) => group.querySelector('[data-option][aria-pressed="true"]')?.dataset.optionValue || '');
  }

  #selectOption(option) {
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;
    const group = option.closest('[data-option-group]');
    group?.querySelectorAll('[data-option]').forEach((candidate) => {
      candidate.setAttribute('aria-pressed', String(candidate === option));
    });

    const selectedOptions = this.#selectedOptions();
    const variant = this.#variants.find((candidate) => candidate.options.every((value, index) => value === selectedOptions[index]));
    this.#applyVariant(variant);
    this.#updateOptionAvailability();
  }

  #updateOptionAvailability() {
    const selected = this.#selectedOptions();
    this.querySelectorAll('[data-option]').forEach((option) => {
      if (!(option instanceof HTMLButtonElement)) return;
      const position = Number(option.dataset.optionPosition);
      const candidateOptions = [...selected];
      candidateOptions[position] = option.dataset.optionValue || '';
      const available = this.#variants.some((variant) => variant.available && variant.options.every((value, index) => value === candidateOptions[index]));
      option.disabled = !available && option.getAttribute('aria-pressed') !== 'true';
      option.setAttribute('aria-disabled', String(option.disabled));
    });
  }

  #applyVariant(variant) {
    const variantInput = this.querySelector('[data-variant-input]');
    const addButton = this.querySelector('[data-add-button]');
    const addLabel = this.querySelector('[data-add-label]');
    const buyButton = this.querySelector('[data-buy-now]');
    const productForm = this.querySelector('product-form-component');
    const error = this.querySelector('[data-form-error]');

    if (!(variantInput instanceof HTMLInputElement) || !(addButton instanceof HTMLButtonElement) || !(buyButton instanceof HTMLButtonElement)) return;

    if (!variant) {
      variantInput.value = '';
      addButton.disabled = true;
      buyButton.disabled = true;
      if (addLabel) addLabel.textContent = this.dataset.unavailableLabel || 'Unavailable';
      this.#syncStickyVariant(null);
      return;
    }

    variantInput.value = String(variant.id);
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    addButton.disabled = !variant.available;
    buyButton.disabled = !variant.available;
    if (addLabel) addLabel.textContent = variant.available ? (this.dataset.addLabel || 'Add to cart') : (this.dataset.soldOutLabel || 'Sold out');
    if (error) error.textContent = '';

    const price = this.querySelector('[data-price]');
    const compare = this.querySelector('[data-compare]');
    const discount = this.querySelector('[data-discount]');
    if (price) price.textContent = variant.price;

    const hasComparePrice = Number(variant.comparePriceValue) > Number(variant.priceValue);
    if (compare) {
      compare.textContent = hasComparePrice ? variant.comparePrice : '';
      compare.toggleAttribute('hidden', !hasComparePrice);
    }
    if (discount) {
      const percentage = hasComparePrice ? Math.round(((variant.comparePriceValue - variant.priceValue) / variant.comparePriceValue) * 100) : 0;
      discount.textContent = `${percentage}% OFF`;
      discount.toggleAttribute('hidden', percentage <= 0);
    }

    if (productForm instanceof HTMLElement) productForm.dataset.quantityDefault = String(variant.minimum || 1);
    this.#updateQuantity(variant);

    if (variant.featuredMediaId) {
      const thumb = Array.from(this.querySelectorAll('[data-thumb]')).find((candidate) => candidate.dataset.mediaId === String(variant.featuredMediaId));
      if (thumb) this.#moveThumbToFront(thumb);
      else this.#selectMedia(variant.featuredMediaId);
    }

    const addContainer = this.querySelector('add-to-cart-component');
    const activeImage = this.querySelector('[data-slide].is-active img');
    if (addContainer instanceof HTMLElement && activeImage instanceof HTMLImageElement) {
      addContainer.dataset.productVariantMedia = activeImage.currentSrc || activeImage.src;
    }
    this.#syncStickyVariant(variant);
  }

  #updateQuantity(variant) {
    const selector = this.querySelector('quantity-selector-component');
    const input = selector?.querySelector('input[name="quantity"]');
    if (!(selector instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

    selector.dataset.variantId = String(variant.id);
    input.disabled = !variant.available;
    input.dataset.min = String(variant.minimum || 1);
    input.dataset.cartQuantity = '0';
    input.min = String(variant.minimum || 1);
    input.step = String(variant.increment || 1);
    if (variant.maximum) input.max = String(variant.maximum);
    else input.removeAttribute('max');

    const minimum = Number(variant.minimum) || 1;
    const increment = Number(variant.increment) || 1;
    let nextValue = Math.max(minimum, Number(input.value) || minimum);
    nextValue = minimum + Math.floor((nextValue - minimum) / increment) * increment;
    if (variant.maximum) nextValue = Math.min(nextValue, Number(variant.maximum));
    input.value = String(nextValue);

    if ('serverDisabledMinus' in selector) selector.serverDisabledMinus = false;
    if ('serverDisabledPlus' in selector) selector.serverDisabledPlus = false;
    selector.querySelectorAll('button').forEach((button) => { button.disabled = !variant.available; });
    if (typeof selector.updateConstraints === 'function') {
      selector.updateConstraints(String(minimum), variant.maximum ? String(variant.maximum) : null, String(increment));
    }
  }

  #buyNow(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const variantInput = this.querySelector('[data-variant-input]');
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (!(variantInput instanceof HTMLInputElement) || !variantInput.value) return;

    const cartUrl = button.dataset.cartUrl || '/cart';
    const quantity = Math.max(1, Number(quantityInput?.value) || 1);
    const checkoutUrl = `${cartUrl}/${encodeURIComponent(variantInput.value)}:${quantity}?checkout`;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    if (window.Shopify?.designMode) {
      const checkoutWindow = window.open(checkoutUrl, '_blank', 'noopener');
      if (checkoutWindow) {
        window.setTimeout(() => {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }, 500);
        return;
      }
    }

    window.location.assign(checkoutUrl);
  }

  #handleCartLinesUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const button = this.querySelector('[data-add-button]');
    const stickyButton = this.querySelector('[data-sticky-add]');
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    if (stickyButton instanceof HTMLButtonElement) {
      stickyButton.setAttribute('aria-busy', 'true');
      stickyButton.disabled = true;
    }

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        if (typeof window.refreshAndOpenKushaCartDrawer === 'function') {
          window.refreshAndOpenKushaCartDrawer();
          return;
        }
        const openCart = window.Shopify?.actions?.openCart;
        if (typeof openCart === 'function') {
          openCart();
          return;
        }
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      })
      .catch(() => {})
      .finally(() => {
        if (button instanceof HTMLButtonElement) {
          button.removeAttribute('aria-busy');
          const variant = this.#variants.find((candidate) => String(candidate.id) === this.querySelector('[data-variant-input]')?.value);
          button.disabled = !variant?.available;
          if (stickyButton instanceof HTMLButtonElement) {
            stickyButton.removeAttribute('aria-busy');
            stickyButton.disabled = !variant?.available;
          }
        }
      });
  };
}

if (!customElements.get('sk-custom-product')) {
  customElements.define('sk-custom-product', SkCustomProduct);
}
